import { useCallback, useState } from 'react';

import { Step } from '../types';

interface UseStepperArgs {
  initialStep?: Step;
  onCancel: () => void;
}

export const useStepper = ({ initialStep = Step.Base, onCancel }: UseStepperArgs) => {
  const [currentStep, setCurrentStep] = useState(initialStep);

  const getStepperItemState = useCallback(
    (stepIndex: number) => {
      // stepIndex is 1-based from Stepper component
      // Convert to 0-based and adjust for standalone mode
      const step = initialStep === Step.PreSelect ? stepIndex - 1 : stepIndex;

      if (step === currentStep) {
        return { isActive: true };
      }

      if (step < currentStep) {
        return { isCompleted: true };
      }

      return {};
    },
    [currentStep, initialStep],
  );

  const onPrevious = useCallback(
    () =>
      setCurrentStep((current) => {
        if (current === Step.PreSelect) {
          onCancel();
          return current;
        }

        if (current === Step.Base) {
          if (initialStep === Step.PreSelect) {
            return Step.PreSelect;
          }
          onCancel();
          return current;
        }

        if (current === Step.Type) {
          return Step.Base;
        }

        if (current === Step.Config) {
          return Step.Type;
        }

        return current;
      }),
    [initialStep, onCancel],
  );

  const onNext = useCallback(
    () =>
      setCurrentStep((current) => {
        return Math.min(current + 1, Step.Config) as Step;
      }),
    [],
  );

  return { currentStep, getStepperItemState, onNext, onPrevious };
};
